import type { components } from '@/lib/api/generated'

type POItem = components['schemas']['POLineItemResponse']

interface SessionSummaryProps {
  items: POItem[]
  // Net units received during THIS receiving session, keyed by po_item_id.
  // Accumulated client-side from each successful scan/manual receive so we can
  // show what changed this visit — qty_received on the item itself is the
  // cumulative total across every session and can't answer "what did I just do".
  sessionReceipts: Record<number, number>
}

function ItemLabel({ item }: { item: POItem }) {
  return (
    <div className="min-w-0 flex-1">
      <div className="truncate text-xs font-medium text-[var(--text-primary)]">
        {item.product_title}
        {item.variant_title && (
          <span className="text-[var(--mute)]"> · {item.variant_title}</span>
        )}
      </div>
      {item.sku && (
        <div className="font-mono text-[10px] text-[var(--mute)]">{item.sku}</div>
      )}
    </div>
  )
}

// Shown inside the Save & exit and Finish & archive dialogs. Two sections:
// what was received during this session, and what's still short — so a partial
// receive is legible at a glance ("here's what I scanned, here's what I didn't").
export function SessionSummary({ items, sessionReceipts }: SessionSummaryProps) {
  const received = items
    .map((item) => ({ item, qty: sessionReceipts[item.po_item_id] ?? 0 }))
    .filter((r) => r.qty > 0)
  const receivedUnits = received.reduce((s, r) => s + r.qty, 0)

  const shortItems = items.filter((it) => it.qty_received < it.qty_ordered)

  return (
    <div
      className="flex flex-col gap-3 text-[var(--text-primary)]"
      data-testid="session-summary"
    >
      {/* Received this session */}
      <section>
        <div className="mb-1 flex items-baseline gap-1.5">
          <h3 className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--mute)]">
            Received this session
          </h3>
          <span className="text-[10px] tabular-nums text-[var(--mute)]">
            {receivedUnits} unit{receivedUnits === 1 ? '' : 's'} · {received.length}{' '}
            item{received.length === 1 ? '' : 's'}
          </span>
        </div>
        {received.length === 0 ? (
          <p
            className="text-xs text-[var(--mute)]"
            data-testid="session-received-empty"
          >
            No items received this session.
          </p>
        ) : (
          <ul className="max-h-[300px] divide-y divide-[var(--line-warm)] overflow-auto border border-[var(--line-warm)]">
            {received.map(({ item, qty }) => (
              <li
                key={item.po_item_id}
                className="flex items-center gap-2 px-2 py-1.5"
                data-testid={`session-received-${item.po_item_id}`}
              >
                <ItemLabel item={item} />
                <div className="shrink-0 text-right">
                  <div className="text-xs font-semibold tabular-nums text-[var(--status-approved-fg)]">
                    +{qty}
                  </div>
                  <div className="text-[10px] tabular-nums text-[var(--mute)]">
                    {item.qty_received}/{item.qty_ordered}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Still short / not received */}
      <section>
        <div className="mb-1 flex items-baseline gap-1.5">
          <h3 className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--mute)]">
            Still short / not received
          </h3>
          <span className="text-[10px] tabular-nums text-[var(--mute)]">
            {shortItems.length} item{shortItems.length === 1 ? '' : 's'}
          </span>
        </div>
        {shortItems.length === 0 ? (
          <p
            className="text-xs text-[var(--mute)]"
            data-testid="session-short-empty"
          >
            Nothing outstanding — all ordered units received.
          </p>
        ) : (
          <ul className="max-h-[300px] divide-y divide-[var(--line-warm)] overflow-auto border border-[var(--line-warm)]">
            {shortItems.map((item) => {
              const remaining = item.qty_ordered - item.qty_received
              const partial = item.qty_received > 0
              return (
                <li
                  key={item.po_item_id}
                  className="flex items-center gap-2 px-2 py-1.5"
                  data-testid={`session-short-${item.po_item_id}`}
                >
                  <ItemLabel item={item} />
                  <div className="shrink-0 text-right">
                    <div className="text-xs font-semibold tabular-nums text-[var(--status-awaiting-fg)]">
                      {remaining} short
                    </div>
                    <div className="text-[10px] tabular-nums text-[var(--mute)]">
                      {partial ? `${item.qty_received}/${item.qty_ordered}` : 'not received'}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </section>
    </div>
  )
}
